/**
 * Pay Period Utilities
 * Central location for date calculation functions used by pay period presets.
 * Uses native JS Date only (no date-fns) per project convention.
 */

// Format date as YYYY-MM-DD
const formatDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Get start of week (Sunday)
const getStartOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0, 0, 0, 0);
  return d;
};

// Get end of week (Saturday)
const getEndOfWeek = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() + (6 - d.getDay()));
  d.setHours(23, 59, 59, 999);
  return d;
};

// Get start of month (1st)
const getStartOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Get end of month (last day)
const getEndOfMonth = (date) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Subtract weeks from date
const subWeeks = (date, weeks) => {
  const d = new Date(date);
  d.setDate(d.getDate() - weeks * 7);
  return d;
};

// Subtract months from date
const subMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
};

/**
 * All available period types for the admin dropdown.
 */
export const PERIOD_TYPES = [
  { value: 'all_time', label: 'All Time' },
  { value: 'current_week', label: 'Current Week (Sun–Sat)' },
  { value: 'previous_week', label: 'Previous Week (Sun–Sat)' },
  { value: 'biweekly', label: 'Bi-Weekly (rolling 14 days)' },
  { value: 'semi_monthly_current', label: 'Semi-Monthly Current (1st–15th or 16th–end)' },
  { value: 'semi_monthly_previous', label: 'Semi-Monthly Previous' },
  { value: 'current_month', label: 'Current Month' },
  { value: 'previous_month', label: 'Previous Month' },
];

/**
 * Calculate start/end dates for a given period_type.
 * Returns { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' } or { start: '', end: '' } for all_time.
 */
export function calculatePeriodDates(periodType) {
  const today = new Date();

  switch (periodType) {
    case 'all_time':
      return { start: '', end: '' };

    case 'current_week':
      return {
        start: formatDate(getStartOfWeek(today)),
        end: formatDate(getEndOfWeek(today)),
      };

    case 'previous_week': {
      const lastWeek = subWeeks(today, 1);
      return {
        start: formatDate(getStartOfWeek(lastWeek)),
        end: formatDate(getEndOfWeek(lastWeek)),
      };
    }

    case 'biweekly':
      return {
        start: formatDate(getStartOfWeek(subWeeks(today, 1))),
        end: formatDate(getEndOfWeek(today)),
      };

    case 'semi_monthly_current': {
      const day = today.getDate();
      if (day <= 15) {
        return {
          start: formatDate(getStartOfMonth(today)),
          end: formatDate(new Date(today.getFullYear(), today.getMonth(), 15)),
        };
      }
      return {
        start: formatDate(new Date(today.getFullYear(), today.getMonth(), 16)),
        end: formatDate(getEndOfMonth(today)),
      };
    }

    case 'semi_monthly_previous': {
      const day2 = today.getDate();
      if (day2 <= 15) {
        const lastMonth = subMonths(today, 1);
        return {
          start: formatDate(new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 16)),
          end: formatDate(getEndOfMonth(lastMonth)),
        };
      }
      return {
        start: formatDate(getStartOfMonth(today)),
        end: formatDate(new Date(today.getFullYear(), today.getMonth(), 15)),
      };
    }

    case 'current_month':
      return {
        start: formatDate(getStartOfMonth(today)),
        end: formatDate(getEndOfMonth(today)),
      };

    case 'previous_month': {
      const lastMonth = subMonths(today, 1);
      return {
        start: formatDate(getStartOfMonth(lastMonth)),
        end: formatDate(getEndOfMonth(lastMonth)),
      };
    }

    default:
      return { start: '', end: '' };
  }
}

/**
 * Transform API presets into dropdown-ready array.
 * Each item has: { value, label, description, includeUnpaidPrevious, getValue }
 *   - value: unique string id (preset DB id as string)
 *   - label: display name
 *   - description: tooltip text
 *   - includeUnpaidPrevious: boolean - also show unpaid entries before the period start
 *   - getValue(): returns { start, end } date strings
 */
export function buildPresetsFromApi(apiPresets) {
  if (!apiPresets || apiPresets.length === 0) {
    // Fallback: return a single "All Time" option
    return [{ value: 'all', label: 'All Time', description: 'Show all entries with no date filter', includeUnpaidPrevious: false, getValue: () => ({ start: '', end: '' }) }];
  }

  return apiPresets.map((preset) => ({
    value: String(preset.id),
    label: preset.name,
    description: preset.description || '',
    includeUnpaidPrevious: !!preset.include_unpaid_previous,
    getValue: () => calculatePeriodDates(preset.period_type),
  }));
}
